// ==================== HeroSection.js ====================
import React from 'react';
import { MousePointer, ChevronDown } from 'lucide-react';
import Button from './Buttons';
import { ReactComponent as Microsoft } from '../assets/microsoft.svg';
import { ReactComponent as Netflix } from '../assets/netflix.svg';
import { ReactComponent as Meta } from '../assets/meta.svg';
import { ReactComponent as Deloitte } from '../assets/deloitte.svg';
import { ReactComponent as JPMorgan } from '../assets/jp-morgan.svg';
import { ReactComponent as McKinsey } from '../assets/mckinsey.svg';
import { ReactComponent as Tesla } from '../assets/tesla.svg';
import { ReactComponent as Uber } from '../assets/uber.svg';

const HeroSection = ({ isVisible, scrollToSection }) => {
  const jobListings = [
    {
      title: "Machine Learning Engineer",
      company: "Microsoft",
      logo: Microsoft,
      logoType: "component",
      salary: "$180K - $250K",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "Product Manager",
      company: "Meta",
      logo: Meta,
      logoType: "component",
      salary: "$160K - $220K",
      location: "Hybrid",
      type: "Full-time"
    },
    {
      title: "Software Engineer",
      company: "Netflix",
      logo: Netflix,
      logoType: "component",
      salary: "$140K - $200K",
      location: "On-site",
      type: "Full-time"
    },
    {
      title: "Data Scientist",
      company: "Tesla",
      logo: Tesla,
      logoType: "component",
      salary: "$150K - $210K",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "UX Designer",
      company: "JPMorgan Chase",
      logo: JPMorgan,
      logoType: "component",
      salary: "$130K - $180K",
      location: "Hybrid",
      type: "Full-time"
    },
    {
      title: "Business Analyst",
      company: "Deloitte",
      logo: Deloitte,
      logoType: "component",
      salary: "$95K - $140K",
      location: "On-site",
      type: "Contract"
    },
    {
      title: "Frontend Developer",
      company: "Uber",
      logo: Uber,
      logoType: "component",
      salary: "$120K - $170K",
      location: "Remote",
      type: "Full-time"
    },
    {
      title: "DevOps Engineer",
      company: "McKinsey",
      logo: McKinsey,
      logoType: "component",
      salary: "$135K - $190K",
      location: "Hybrid",
      type: "Contract"
    }
  ];

  // Duplicate the array for seamless scrolling
  const duplicatedJobs = [...jobListings, ...jobListings];

  const getLocationColor = (location) => {
    switch(location) {
      case 'Remote': return 'bg-blue-100 text-blue-700';
      case 'Hybrid': return 'bg-purple-100 text-purple-700';
      case 'On-site': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <section id="section-0" className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto text-center">
        <div className={`transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'}`}>
          
          {/* Main Heading */}
          <h1 className="text-6xl lg:text-8xl font-bold leading-tight mb-8 text-gray-900">
            <div className="mt-16 mb-8">Stop applying.</div>
            <div className="mt-12">
              <span style={{
                background: 'linear-gradient(45deg, #06b6d4, #8b5cf6, #ec4899, #06b6d4)',
                backgroundSize: '300% 300%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: 'gradientShift 3s ease-in-out infinite'
              }}>
                Start getting hired.
              </span>
            </div>
          </h1>
          
          {/* Subtitle */}
          <p className="text-xl lg:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            We rewrite your LinkedIn, apply for the right roles, and connect you directly to hiring managers — 
            so you can focus on landing your next opportunity.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-20">
            <Button className="text-lg px-8 py-4">
              <MousePointer size={20} />
              Start Your Journey
            </Button>
          </div>

          {/* We're Currently Hiring For Section */}
<div className="mb-20">
  <h3 className="text-2xl font-semibold text-gray-700 mb-8">Placing jobs for</h3>
  <div className="relative overflow-hidden">
    <div className="flex animate-scroll gap-6">
      {duplicatedJobs.map((job, index) => (
        <div
          key={index}
          className="flex-shrink-0 bg-white rounded-xl px-6 py-5 shadow-lg border border-gray-200 min-w-[320px] hover:shadow-xl transition-shadow duration-300"
        >
          {/* Top Row: Logo | Job Info */}
          <div className="flex items-start gap-4 mb-4">
            {/* Logo (larger, no white border) */}
            <div className="w-16 h-16 rounded-md overflow-hidden flex items-center justify-center">
              {job.logoType === 'component' ? (
                <job.logo className="w-12 h-12 object-contain" />
              ) : (
                <img
                  src={job.logo}
                  alt={`${job.company} logo`}
                  className="w-12 h-12 object-contain"
                />
              )}
            </div>

            {/* Job Title + Company */}
            <div className="flex-1">
              <h4 className="font-bold text-gray-900 text-lg leading-snug text-left">{job.title}</h4>
              <p className="text-sm text-gray-600 mt-1 text-left">{job.company}</p>
            </div>
          </div>

          {/* Bottom Row: Salary | Type + Location */}
          <div className="flex items-center justify-between">
            <div className="text-lg font-bold text-green-600">{job.salary}</div>
            <div className="text-right">
              <div className="text-sm text-gray-700">{job.type}</div>
              <div
                className={`text-xs px-2 py-1 rounded-full mt-1 inline-block ${getLocationColor(job.location)}`}
              >
                {job.location}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>


          {/* Success Stories Section */}
          <div className="mb-20">
            <h3 className="text-2xl font-semibold text-gray-700 mb-8">We've already gotten 1000's of people jobs at</h3>
            <div className="flex justify-center items-center gap-8 flex-wrap">
              {[
                { name: "Microsoft", logo: Microsoft, type: "component" },
                { name: "Netflix", logo: Netflix, type: "component" },
                { name: "Meta", logo: Meta, type: "component" },
                { name: "Deloitte", logo: Deloitte, type: "component" },
                { name: "JPMorgan", logo: JPMorgan, type: "component" },
                { name: "Tesla", logo: Tesla, type: "component" },
                { name: "Uber", logo: Uber, type: "component" },
              ].map((company, index) => (
                <div
                key={index}
                className="flex-shrink-0 px-4 py-2 min-w-fit text-center hover:scale-105 transition-transform duration-300"
              >
                <div className="w-20 h-20 mx-auto mb-2 flex items-center justify-center">
                  <company.logo
                    className="w-full h-full object-contain grayscale hover:grayscale-0 transition-all duration-300"
                  />
                </div>
                <h3 className="text-sm font-medium text-gray-800 whitespace-normal">{company.name}</h3>
              </div>
              ))}
            </div>
          </div>

          {/* Scroll Indicator */}
          <button
            onClick={() => scrollToSection(1)}
            className="mt-16 mx-auto w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center hover:bg-gray-50 transition-all duration-300 shadow-lg animate-bounce"
          >
            <ChevronDown size={20} className="text-gray-600" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;